import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Bug, RefreshCw, Library as LibraryIcon, AlertTriangle, Search } from 'lucide-react'
import { libraries, rebuildSearchIndex } from '../api/client'

export default function Debug() {
  const [status, setStatus] = useState<string | null>(null)

  const { data: libraryList, isLoading } = useQuery({
    queryKey: ['libraries'],
    queryFn: () => libraries.list(),
  })

  const rescanMutation = useMutation({
    mutationFn: (libraryId: string) => libraries.rescan(libraryId),
    onSuccess: (data) => {
      setStatus(`Rescan started for library ${data.library_id}`)
    },
    onError: (error: Error) => {
      setStatus(`Error: ${error.message}`)
    },
  })

  const rebuildSearchMutation = useMutation({
    mutationFn: () => rebuildSearchIndex(),
    onSuccess: (data) => {
      setStatus(`Search index rebuilt with ${data.count} entries`)
    },
    onError: (error: Error) => {
      setStatus(`Error: ${error.message}`)
    },
  })

  const musicLibraries = libraryList?.filter(lib => lib.library_type === 'music') || []

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Bug size={24} />
        Debug Tools
      </h1>

      <div className="card mb-6">
        <div className="flex items-center gap-2 text-yellow-400 mb-4">
          <AlertTriangle size={20} />
          <span className="text-sm">These tools are for debugging and maintenance. Use with caution.</span>
        </div>
      </div>

      {/* Rebuild Search Index */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Search size={20} />
          Rebuild Search Index
        </h2>
        <p className="text-dark-400 text-sm mb-4">
          Rebuild the search index to include all artists, albums, tracks, movies, and series.
          Run this if search results are missing items.
        </p>
        <button
          onClick={() => rebuildSearchMutation.mutate()}
          disabled={rebuildSearchMutation.isPending}
          className="btn btn-primary flex items-center gap-2"
        >
          <Search size={16} className={rebuildSearchMutation.isPending ? 'animate-pulse' : ''} />
          {rebuildSearchMutation.isPending ? 'Rebuilding...' : 'Rebuild Search Index'}
        </button>
      </div>

      {/* Library Rescan */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <RefreshCw size={20} />
          Library Rescan
        </h2>
        <p className="text-dark-400 text-sm mb-4">
          Rescan a music library to update all track metadata (artist, album, cover art) from file tags and folder structure.
          This will update existing tracks, not add new ones.
        </p>

        {isLoading ? (
          <div className="text-dark-400">Loading libraries...</div>
        ) : musicLibraries.length === 0 ? (
          <div className="text-dark-400">No music libraries found</div>
        ) : (
          <div className="space-y-3">
            {musicLibraries.map((lib) => (
              <div key={lib.id} className="flex items-center justify-between bg-dark-700 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <LibraryIcon size={20} className="text-primary-400" />
                  <div>
                    <p className="font-medium">{lib.name}</p>
                    <p className="text-dark-400 text-sm">{lib.path}</p>
                  </div>
                </div>
                <button
                  onClick={() => rescanMutation.mutate(lib.id)}
                  disabled={rescanMutation.isPending}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <RefreshCw size={16} className={rescanMutation.isPending ? 'animate-spin' : ''} />
                  Rescan
                </button>
              </div>
            ))}
          </div>
        )}

        {status && (
          <div className={`mt-4 p-3 rounded-lg ${status.startsWith('Error') ? 'bg-red-900/20 text-red-400' : 'bg-green-900/20 text-green-400'}`}>
            {status}
          </div>
        )}
      </div>
    </div>
  )
}
